import { Selection, EnterElement } from "d3"
import { D3VisualizationRenderer } from "../../D3VisualizationRenderer"
import { D3ClipPath } from "../../../D3/D3ClipPath"
import { throttledRender } from "../../../../rendering"
import { D3Timeline } from "../../../D3/Timeline/D3Timeline"
import { D3ValueAxis } from "../../../D3/D3ValueAxis"
import { D3HorizontalLines } from "../../../D3/D3HorizontalLines"
import { D3Parabola } from "../../../D3/D3Parabola"
import { LEFT_MARGIN } from "../../Constants"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { MobergAnimationTimingMs, MobergFontFamily } from "../../../../../../../../Moberg"
import { DataSource } from "../../../../Types/DataSource"
import { CompositeTimeSeriesData, findIndexOfClosestValidData } from "../../../../Data/TimeSeriesData"
import { D3SummaryValues } from "../../../D3/D3SummaryValues"
import { D3Line } from "../../../D3/D3Line"
import { D3LinearXAxis } from "../../../D3/D3LinearXAxis"
import { D3BoxPlot } from "../../../D3/D3BoxPlot"
import { D3Rect } from "../../../D3/D3Rect"
import { D3MAPOptPlot } from "./D3MAPOptPlot"
import { D3MAPOptPlotConfigurationBuilder } from "./D3MAPOptConfigurationBuilder"
import { D3ErrorBars } from "../../CPPOpt/D3/D3CPPOptErrorBars"
import { MAPOptPlotConfig } from "../../../../Types/MAPOptPlot"
import { Analytic } from "../../../../Types/AnalysisDetails"
import { formatDate } from "../../../../utils"

export class D3MAPOptPlotRenderer extends D3VisualizationRenderer<D3MAPOptPlot, D3MAPOptPlotConfigurationBuilder> {
	// CHILDREN
	private clipPath?: D3ClipPath
	private xAxis?: D3LinearXAxis
	private yAxis?: D3ValueAxis
	private horizontalLines?: D3HorizontalLines
	private parabola?: D3Parabola
	private errorBars?: D3ErrorBars
	private curveFitValues?: D3SummaryValues
	private prxLine?: D3Line
	private llaRect?: D3Rect
	private ulaRect?: D3Rect
	private mapoptLine?: D3Line
	private mapoptBoxPlot?: D3BoxPlot
	private coxBoxPlot?: D3BoxPlot
	private percentagesSummaryValues?: D3SummaryValues

	private colorSchemeObserver: MutationObserver

	constructor(visualization: D3MAPOptPlot, configBuilder: D3MAPOptPlotConfigurationBuilder, className: string) {
		super(visualization, configBuilder, className)

		this.colorSchemeObserver = new MutationObserver((mutations) => {
			for (const mutation of mutations) {
				if (mutation.attributeName === "data-color-scheme") {
					this.updateChildren()
					this.render()
					break
				}
			}
		})
		this.colorSchemeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-color-scheme"] })
	}

	public destroy() {
		this.colorSchemeObserver.disconnect()
	}

	private timelineSpacing = 70
	private titleSpace = 20
	private timelineContainerClassName = "d3-mapopt-plot-timeline"
	private curveFitPlotClassName = "d3-mapopt-curve-fit-plot"
	private summaryValueContainerClassName = "d3-mapopt-plot-summary-value-container"
	private mapoptBoxPlotContainerClassName = "d3-mapopt-box-plot-container"
	private curveFitTitleClassName = "d3-mapopt-plot-title-text"
	private curveFitSubTitleClassName = "d3-mapopt-plot-subtitle-text"
	private summaryValuesTitleClassName = "d3-mapopt-plot-summary-values-title"
	private summaryValuesSubTitleClassName = "d3-mapopt-plot-summary-values-subtitle"
	private curveFitValuesContainerClassName = "d3-mapopt-plot-curve-fit-values"
	private coxBoxPlotContainerClassName = "d3-mapopt-plot-prx-box-plot-container"
	private percentagesSummaryValuesClassName = "d3-mapopt-plot-percentages-summary-values"

	public onYAxisDragStart(): void {
		this.configBuilder.transitionDuration = 0
		this.updateChildren()
	}

	public onYAxisDrag(): void {
		this.horizontalLines?.render()
		this.parabola?.render()
		this.errorBars?.render()
		this.prxLine?.updateConfig(this.configBuilder.getCOxLineConfig())
	}

	public onYAxisDragEnd(): void {
		this.configBuilder.transitionDuration = MobergAnimationTimingMs.SLOW
		this.updateChildren()
	}

	public viewTimesChanged(): void {
		this.timeline?.viewTimesChanged()
		this.updateMAPOptData()
	}

	public onTimelineSliderDrag(): void {
		this.updateMAPOptData()
	}

	public renderPage(page: ModalityPage) {
		this.updateMAPOptData()
	}

	protected canRender(): boolean {
		return true // this.visualization.boundingBox.height > 0 && this.visualization.boundingBox.width > 0
	}

	protected enter(enterElements: Selection<EnterElement, any, any, any>): Selection<any, any, any, any> {
		this.updateMAPOptData()
		
		const svg = enterElements
			.append("svg")
			.attr("class", this.className)
			.attr("width", "100%")
			.attr("height", "100%")

		const curveFitContainer = svg
			.append("g")
			.attr("class", this.curveFitPlotClassName)
			.attr("transform", `translate(${this.visualization.curveFitBoundingBox.x}, ${this.visualization.curveFitBoundingBox.y})`)

		curveFitContainer
			.append("text")
			.attr("class", this.curveFitTitleClassName) 
			.style("font-family", MobergFontFamily.HEADER)
			.style("font-size", 18)
			.style("text-anchor", "middle")
			.style("fill", "var(--color-chart-axis-text, black)")
			.text("Current MAPopt Curve Fit")
			.attr("transform", `translate(${this.visualization.curveFitBoundingBox.width / 2}, -${2*this.titleSpace})`)

		curveFitContainer
			.append("text")
			.attr("class", this.curveFitSubTitleClassName) 
			.style("font-family", MobergFontFamily.HEADER)
			.style("font-size", 14)
			.style("text-anchor", "middle")
			.style("fill", "var(--color-chart-axis-text, black)")
			.text(this.getMAPoptStartEndString())
			.attr("transform", `translate(${this.visualization.curveFitBoundingBox.width / 2}, -${this.titleSpace})`)

		const curveFitValuesContainer = svg
			.append("g")
			.attr("class", this.curveFitValuesContainerClassName)
			.attr("transform", `translate(${this.visualization.curveFitBoundingBox.x + this.visualization.curveFitBoundingBox.width}, ${this.visualization.curveFitBoundingBox.y})`)

		const timelineContainer = svg
			.append("g")
			.attr("class", this.timelineContainerClassName)
			.attr("transform", `translate(${LEFT_MARGIN}, ${this.visualization.curveFitBoundingBox.y + this.visualization.curveFitBoundingBox.height + this.timelineSpacing})`)

		const summaryValuesContainer = svg
			.append("g")
			.attr("class", this.summaryValueContainerClassName)
			.attr("transform", `translate(${this.visualization.config.dimensions.width/2}, ${this.visualization.summaryValuesBoundingBox.y})`)

		const mapoptBoxPlotContainer = summaryValuesContainer
			.append("g")
			.attr("class", this.mapoptBoxPlotContainerClassName)
			.attr("transform", `translate(${this.visualization.mapoptBoxPlotBoundingBox.x}, ${this.visualization.mapoptBoxPlotBoundingBox.y})`)

		const coxBoxPlotContainer = summaryValuesContainer
			.append("g")
			.attr("class", this.coxBoxPlotContainerClassName)
			.attr("transform", `translate(${this.visualization.config.dimensions.width/4 + this.visualization.coxBoxPlotBoundingBox.x}, ${this.visualization.coxBoxPlotBoundingBox.y})`)

		const percentagesSummaryValuesContainer = svg
			.append("g")
			.attr("class", this.percentagesSummaryValuesClassName)
			.attr("transform", `translate(${this.visualization.config.dimensions.width/2}, ${7*this.visualization.config.dimensions.height/8 - this.timelineSpacing - 20})`)

		summaryValuesContainer
			.append("text")
			.attr("class", this.summaryValuesTitleClassName) 
			.style("font-family", MobergFontFamily.HEADER)
			.style("font-size", 18)
			.style("text-anchor", "middle")
			.style("fill", "var(--color-chart-axis-text, black)")
			.text("Summary")
			.attr("transform", `translate(${this.visualization.summaryValuesBoundingBox.width/2 + 20}, -${2*this.titleSpace})`)

		summaryValuesContainer
			.append("text")
			.attr("class", this.summaryValuesSubTitleClassName) 
			.style("font-family", MobergFontFamily.HEADER)
			.style("font-size", 14)
			.style("text-anchor", "middle")
			.style("fill", "var(--color-chart-axis-text, black)")
			.text(this.getStartEndDateString())
			.attr("transform", `translate(${this.visualization.summaryValuesBoundingBox.width/2 + 20}, -${this.titleSpace})`)

		curveFitContainer.each((config, index, nodes) => this.createCurveFitBoundingBoxChildren(config, index, nodes))
		timelineContainer.each((config, index, nodes) => this.createTimeline(config, index, nodes))
		mapoptBoxPlotContainer.each((config, index, nodes) => this.createMAPoptBoxPlotContainerChildren(config, index, nodes))
		coxBoxPlotContainer.each((config, index, nodes) => this.createPRxBoxPlotContainerChildren(config, index, nodes))
		curveFitValuesContainer.each((config, index, nodes) => this.createCurveFitValuesChildren(config, index, nodes))
		percentagesSummaryValuesContainer.each((config, index, nodes) => this.createPercentagesSummaryValues(config, index, nodes))

		this.createClipPath(svg.node() as SVGSVGElement)
		this.renderChildren()

		return svg
	}

	private createCurveFitBoundingBoxChildren(config: MAPOptPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) {
		const root = nodes[index]

		this.yAxis = new D3ValueAxis(root, this.configBuilder.getYAxisConfig(), this.visualization.reactCallbacks)
		this.xAxis = new D3LinearXAxis(root, this.configBuilder.getXAxisConfig(), this.visualization.reactCallbacks)
		this.horizontalLines = new D3HorizontalLines(root, this.configBuilder.getHorizontalLinesConfig())
		this.parabola = new D3Parabola(root, this.configBuilder.getParabolaConfig(), this.visualization.reactCallbacks)
		this.errorBars = new D3ErrorBars(root, this.configBuilder.getErrorBarsConfig(), this.visualization.reactCallbacks)
		this.prxLine = new D3Line(root, this.configBuilder.getCOxLineConfig(), "d3-mapopt-plot-prx-line", this.visualization.reactCallbacks)
		this.llaRect = new D3Rect(root, this.configBuilder.getLLARectConfig(), "d3-mapopt-plot-lla-line", this.visualization.reactCallbacks)
		this.ulaRect = new D3Rect(root, this.configBuilder.getULARectConfig(), "d3-mapopt-plot-ula-line", this.visualization.reactCallbacks)
		this.mapoptLine = new D3Line(root, this.configBuilder.getMAPOptLineConfig(), "d3-mapopt-plot-mapopt-line", this.visualization.reactCallbacks)
	}

	private createCurveFitValuesChildren(config: MAPOptPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) {
		const root = nodes[index]
		this.curveFitValues = new D3SummaryValues(root, this.configBuilder.getSummaryValuesConfig(), "d3-mapopt-plot-curve-fit-values", this.visualization.reactCallbacks, this.visualization.pageManager, this.visualization.config.viewScale)
	}

	private createMAPoptBoxPlotContainerChildren(config: MAPOptPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) {
		const root = nodes[index]

		this.mapoptBoxPlot = new D3BoxPlot(root, this.configBuilder.getMAPoptBoxPlotConfig(), "d3-mapopt-plot-mapopt-box-plot", this.visualization.reactCallbacks, this.visualization.timeSeriesPageManager)
		this.mapoptBoxPlot.render()
	}

	private createPercentagesSummaryValues(config: MAPOptPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) {
		const root = nodes[index]

		this.percentagesSummaryValues = new D3SummaryValues(root, this.configBuilder.getPercentagesSummaryValuesConfig(), "d3-mapopt-plot-aggregated-summary-values", this.visualization.reactCallbacks, this.visualization.timeSeriesPageManager, this.visualization.config.viewScale)
		this.percentagesSummaryValues.render()
	}

	private createPRxBoxPlotContainerChildren(config: MAPOptPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) {
		const root = nodes[index]

		this.coxBoxPlot = new D3BoxPlot(root, this.configBuilder.getCOxBoxPlotConfig(), "d3-mapopt-plot-prx-box-plot", this.visualization.reactCallbacks, this.visualization.timeSeriesPageManager)
		this.coxBoxPlot.render()
	}

	private createClipPath(svg: SVGSVGElement) {
		this.clipPath = new D3ClipPath(svg, this.configBuilder.getClipPathConfig(), this.visualization.reactCallbacks)
	}

	protected update = (updatedElements: Selection<any, any, any, any>): Selection<any, any, any, any> => {
		// Update the summary values container
		const summaryValuesContainer = updatedElements.select("." + this.summaryValueContainerClassName)
			.attr("transform", `translate(${this.visualization.config.dimensions.width/2}, ${this.visualization.summaryValuesBoundingBox.y})`)

		summaryValuesContainer.select("." + this.summaryValuesTitleClassName)
			.attr("transform", `translate(${this.visualization.summaryValuesBoundingBox.width/2 + 20}, -${2*this.titleSpace})`)

			summaryValuesContainer.select("." + this.summaryValuesSubTitleClassName)
			.attr("transform", `translate(${this.visualization.summaryValuesBoundingBox.width/2 + 20}, -${this.titleSpace})`)
			.text(this.getStartEndDateString())

		summaryValuesContainer.select("." + this.mapoptBoxPlotContainerClassName)
			.attr("transform", `translate(${this.visualization.mapoptBoxPlotBoundingBox.x}, ${this.visualization.mapoptBoxPlotBoundingBox.y})`)

		summaryValuesContainer.select("." + this.coxBoxPlotContainerClassName)
			.attr("transform", `translate(${this.visualization.config.dimensions.width/4 + this.visualization.coxBoxPlotBoundingBox.x}, ${this.visualization.coxBoxPlotBoundingBox.y})`)

		// Update the curve fit container
		const curveFitContainer = updatedElements.select("." + this.curveFitPlotClassName)

		curveFitContainer
			.select("." + this.curveFitTitleClassName)
			.attr("transform", `translate(${this.visualization.curveFitBoundingBox.width / 2}, -${2*this.titleSpace})`)

		curveFitContainer
			.select("." + this.curveFitSubTitleClassName)
			.attr("transform", `translate(${this.visualization.curveFitBoundingBox.width / 2}, -${this.titleSpace})`)
			.text(this.getMAPoptStartEndString())

		// Update the curve fit values container
		updatedElements.select("." + this.curveFitValuesContainerClassName)
			.attr("transform", `translate(${this.visualization.curveFitBoundingBox.x + this.visualization.curveFitBoundingBox.width}, ${this.visualization.curveFitBoundingBox.y})`)

		// Update the timeline container
		updatedElements.select("." + this.timelineContainerClassName)
			.attr("transform", `translate(${LEFT_MARGIN}, ${this.visualization.curveFitBoundingBox.y + this.visualization.curveFitBoundingBox.height + this.timelineSpacing})`)

		updatedElements.select("." + this.percentagesSummaryValuesClassName)
			.attr("transform", `translate(${this.visualization.config.dimensions.width/2}, ${7*this.visualization.config.dimensions.height/8 - this.timelineSpacing - 20})`)

		this.renderChildren()

		return updatedElements
	}

	private createTimeline = (config: MAPOptPlotConfig, index: number, nodes: ArrayLike<SVGGElement>) => {
		const root = nodes[index]
		this.timeline = new D3Timeline(root, this.configBuilder.getTimelineConfig(), this.visualization.timeSeriesPageManager, this.visualization.reactCallbacks)
	}

	private renderChildren = () => {
		this.yAxis?.render()
		this.xAxis?.render()
		this.timeline?.render()
		this.horizontalLines?.render()
		this.parabola?.render()
		this.errorBars?.render()
		this.curveFitValues?.render()
		this.percentagesSummaryValues?.render()
		this.mapoptBoxPlot?.updateConfig(this.configBuilder.getMAPoptBoxPlotConfig())
		this.coxBoxPlot?.updateConfig(this.configBuilder.getCOxBoxPlotConfig())
		this.prxLine?.updateConfig(this.configBuilder.getCOxLineConfig())
		this.llaRect?.updateConfig(this.configBuilder.getLLARectConfig())
		this.ulaRect?.updateConfig(this.configBuilder.getULARectConfig())
		this.mapoptLine?.updateConfig(this.configBuilder.getMAPOptLineConfig())
	}

	public updateChildren = () => {
		this.yAxis?.updateConfig(this.configBuilder.getYAxisConfig())
		this.xAxis?.updateConfig(this.configBuilder.getXAxisConfig())
		this.timeline?.updateConfig(this.configBuilder.getTimelineConfig())
		this.clipPath?.updateConfig(this.configBuilder.getClipPathConfig())
		this.parabola?.updateConfig(this.configBuilder.getParabolaConfig())
		this.horizontalLines?.updateConfig(this.configBuilder.getHorizontalLinesConfig())
		this.errorBars?.updateConfig(this.configBuilder.getErrorBarsConfig())
		this.curveFitValues?.updateConfig(this.configBuilder.getSummaryValuesConfig())
		this.percentagesSummaryValues?.updateConfig(this.configBuilder.getPercentagesSummaryValuesConfig())
		this.prxLine?.updateConfig(this.configBuilder.getCOxLineConfig())
		this.llaRect?.updateConfig(this.configBuilder.getLLARectConfig())
		this.ulaRect?.updateConfig(this.configBuilder.getULARectConfig())
		this.mapoptLine?.updateConfig(this.configBuilder.getMAPOptLineConfig())
		this.mapoptBoxPlot?.updateConfig(this.configBuilder.getMAPoptBoxPlotConfig())
		this.coxBoxPlot?.updateConfig(this.configBuilder.getCOxBoxPlotConfig())
		this.updateMAPOptData()
	}

	private mapoptDataChanged = () => {
		this.configBuilder.readMAPOptSignal()
		this.configBuilder.readCOxSignal()
		this.parabola?.updateConfig(this.configBuilder.getParabolaConfig())
		this.errorBars?.updateConfig(this.configBuilder.getErrorBarsConfig())
		this.prxLine?.updateConfig(this.configBuilder.getCOxLineConfig())
		this.llaRect?.updateConfig(this.configBuilder.getLLARectConfig())
		this.ulaRect?.updateConfig(this.configBuilder.getULARectConfig())
		this.mapoptLine?.updateConfig(this.configBuilder.getMAPOptLineConfig())
		this.mapoptBoxPlot?.updateConfig(this.configBuilder.getMAPoptBoxPlotConfig())
		this.coxBoxPlot?.updateConfig(this.configBuilder.getCOxBoxPlotConfig())
		this.curveFitValues?.updateConfig(this.configBuilder.getSummaryValuesConfig())
		this.percentagesSummaryValues?.updateConfig(this.configBuilder.getPercentagesSummaryValuesConfig())
		this.render()
	}

	private updateMAPOptData = throttledRender(() => {
		const [currentPage, latestPage] = this.visualization.timeSeriesPageManager.getPagesInView()
		const dataObjectId = this.visualization.reactCallbacks.dataSourceMap.get(DataSource.CURRENT_PATIENT) as number

		// Go through each of the pages, and try to find the most relevant data point
		let mostRelevantIndex = -1
		let compositeData: CompositeTimeSeriesData | undefined = undefined

		for (const page of [latestPage, currentPage]) {
			compositeData = page?.data.get(dataObjectId)?.get("MAPOpt,Composite,SampleSeries") as CompositeTimeSeriesData | undefined

			if (!compositeData) {
				continue
			}

			const mostRelevantIndexCandidate = findIndexOfClosestValidData(compositeData, this.visualization.config.viewScale.domain()[1].getTime(), 60000, 0)
			
			if (mostRelevantIndexCandidate > -1) {
				mostRelevantIndex = mostRelevantIndexCandidate
				break
			}
		}

		const numBinEdges = 51
		const compositeDataLength = compositeData?.data.length ?? 210
		const latestCompositeData = new Array(compositeDataLength).fill(null)

		if (compositeData && mostRelevantIndex > -1) {
			if (compositeData.data.length !== compositeDataLength) {
				console.warn(`Composite data length was an expected size. Data is incompatible with the display. (expected: ${compositeDataLength}, actual: ${compositeData.data.length})`)
			}

			for (let i = 0; i < compositeData.data.length; i++) {
				latestCompositeData[i] = compositeData.data[i][mostRelevantIndex]
			}
		}

		let offset = 0

		if (this.visualization.config.mapoptAlgorithm === Analytic.MAPOpt_FLEX) {
			offset = 3 // This will give the MAPopt and curve fit information for the highest weighted curve fit out of all multi-windows.
		}

		const windowSizeSeconds = latestCompositeData.at(-1)
		let startTime = null
		let endTime = null

		if (compositeData && windowSizeSeconds) {
			endTime = new Date(compositeData.times[mostRelevantIndex])
			startTime = new Date(compositeData.times[mostRelevantIndex] - (windowSizeSeconds*1000))
		}

		this.visualization.mapoptData = {
			MAPOpt: latestCompositeData[0 + offset],
			LLA: latestCompositeData[1 + offset],
			ULA: latestCompositeData[2 + offset],
			fitType: latestCompositeData[3 + offset],
			fitSpan: latestCompositeData[4 + offset],
			includedDataPercentage: latestCompositeData[5 + offset],
			coefficients: latestCompositeData.slice(6 + offset, 9 + offset),
			yValues: latestCompositeData.slice(9 + offset, 8 + numBinEdges + offset),
			errors: latestCompositeData.slice(8 + numBinEdges + offset, 7 + 2*numBinEdges + offset),
			dataPercentages: latestCompositeData.slice(7 + 2*numBinEdges + offset, 6 + 3*numBinEdges + offset),
			includedInFit: latestCompositeData.slice(6 + 3*numBinEdges + offset, 5 + 4*numBinEdges + offset),
			weight: null,
			startTime,
			endTime
		}

		this.mapoptDataChanged()
	}, this.configBuilder.transitionDuration)

	private getMAPoptStartEndString(): string {
		if (!this.visualization.mapoptData.startTime || !this.visualization.mapoptData.endTime) {
			return ""
		}

		const formattedStart = formatDate(this.visualization.mapoptData.startTime, { timeZone: this.visualization.config.timeZone })
		const formattedEnd = formatDate(this.visualization.mapoptData.endTime, { timeOnly: true, timeZone: this.visualization.config.timeZone, timeZoneName: true })

		return `${formattedStart} - ${formattedEnd}`
	}

	private getStartEndDateString(): string {
		const [start, end] = this.visualization.config.viewScale.domain()

		const formattedStart = formatDate(start, { timeZone: this.visualization.config.timeZone })
		const formattedEnd = formatDate(end, { timeOnly: true, timeZone: this.visualization.config.timeZone, timeZoneName: true })

		return `${formattedStart} - ${formattedEnd}`
	}
}
